// The only file allowed to touch localStorage (lint rule). Preferences only, never business data.

export type ThemePref = 'system' | 'light' | 'dark';

export interface Prefs {
  theme: ThemePref;
  lastBranch: string | null;
}

export const PREFS_KEY = 'ecobox.prefs.v1';

const DEFAULTS: Prefs = { theme: 'system', lastBranch: null };

function isTheme(v: unknown): v is ThemePref {
  return v === 'system' || v === 'light' || v === 'dark';
}

export function readPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw === null) return { ...DEFAULTS };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULTS };
    const rec = parsed as Record<string, unknown>;
    return {
      theme: isTheme(rec.theme) ? rec.theme : DEFAULTS.theme,
      lastBranch: typeof rec.lastBranch === 'string' ? rec.lastBranch : DEFAULTS.lastBranch,
    };
  } catch (err) {
    console.warn('Preferences could not be read; using defaults.', err);
    return { ...DEFAULTS };
  }
}

export function getPref<K extends keyof Prefs>(key: K): Prefs[K] {
  return readPrefs()[key];
}

export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...readPrefs(), [key]: value }));
  } catch (err) {
    console.warn('Preferences could not be saved.', err);
  }
}

export function resetPrefs(): void {
  try {
    localStorage.removeItem(PREFS_KEY);
  } catch (err) {
    console.warn('Preferences could not be cleared.', err);
  }
  try {
    localStorage.removeItem(PINS_KEY);
  } catch {
    /* nothing to clear */
  }
}

export const PINS_KEY = 'ecobox.pins.v1';

/** The pages a person pinned in the office menu (route ids, a UI preference, per person). */
export function getPinnedPages(uid: string): string[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return [];
    const mine = (parsed as Record<string, unknown>)[uid];
    return Array.isArray(mine) ? mine.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function setPinnedPages(uid: string, ids: readonly string[]): void {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    const all = typeof parsed === 'object' && parsed !== null ? (parsed as object) : {};
    localStorage.setItem(PINS_KEY, JSON.stringify({ ...all, [uid]: [...ids] }));
  } catch (err) {
    console.warn('Pinned pages could not be saved.', err);
  }
}
